import get from 'lodash-es/get';
import { GwDatesWrapper } from '../../../../components/_internal_date_/gwDatesWrapper';
import { getResortLocation } from '../../../../store/componentStores/CurrentLodge/currentLodge.selectors';
import {
  getIsOfferAppliedViaPromoBanner,
  getOfferPromoBannerPersonalization
} from '../../../../store/componentStores/Offer/offer.selectors';
import { getInteractionsData, getPromoSearchErrorCode } from '../../../../store/componentStores/Plans/plans.selectors';
import plansActionTypes from '../../../../store/componentStores/Plans/plans.types';
import { getPackageInteractionsData } from '../../../../store/componentStores/Suite/suite.selectors';
import suiteActionTypes from '../../../../store/componentStores/Suite/suite.types';
import { availabilityTrackError } from '../../../../utilities/applicationInsights';
import {
  DATE_FORMATS,
  EVENT_CATEGORIES as GTM_EVENTS,
  ROOM_RATES_ERROR_MSG,
  SEARCH_TYPES,
  SUITE_ERROR_MSG
} from '../../../../utilities/constants';
import { pushEvent } from '../../../../utilities/dataLayerUtils';
import duettoTracking from '../../../../utilities/duetto';
import { getResortShortName } from '../../../../utilities/lodgeData';
import {
  buildAddToCartEnhancedEcommerceObject,
  buildProductClickEnhancedECommerceObject,
  buildSearchObject,
  getPromoErrorEventObject
} from '../analyticsObjectBuilders';
import { getCurrentLocation } from '../utilities/helpers';

export const onPlanListener = ({ type, personalizationRules, personalizationResponses, getState }) => {
  const isAvailabilitySuccess = type === plansActionTypes.GET_PLANS_AVAILABILITY_INFO_SUCCESS;
  const isAvailabilityError = type === plansActionTypes.GET_PLANS_AVAILABILITY_INFO_ERROR;

  if (isAvailabilitySuccess) {
    const { dates, currentLodge } = getState();
    const resortLocation = getResortLocation(currentLodge);
    if (personalizationRules && personalizationRules.length > 0) {
      const interactionsData = getInteractionsData({
        personalizationResponses
      });
      pushEvent({
        event: EVENT_CATEGORIES.personalizationRules,
        rules: personalizationRules.join(),
        ...(interactionsData && {
          aud: interactionsData.userAudience,
          rec: interactionsData.recommendationEngine,
          recType: interactionsData.recommendationType,
          recValue: interactionsData.suiteCategory ? interactionsData.suiteCategory : ''
        })
      });
    }
    duettoTracking.trackAvailabilitySearch({
      resortLocation,
      arrival: dates.checkinDateSelection,
      departure: dates.checkoutDateSelection
    });
  }

  if (isAvailabilityError) {
    const { dates, currentLodge } = getState();
    const resortLocation = getResortLocation(currentLodge);
    duettoTracking.trackAvailabilityDenial({
      resortLocation,
      arrival: dates.checkinDateSelection,
      departure: dates.checkoutDateSelection
    });
  }
};

export const onHighestRatesError = event => {
  const { plans } = event.getState();
  availabilityTrackError(plans, plansActionTypes.GET_PLANS_AVAILABILITY_HIGHEST_RATES_ERROR, SUITE_ERROR_MSG);
  return event;
};
onHighestRatesError.eventType = plansActionTypes.GET_PLANS_AVAILABILITY_HIGHEST_RATES_ERROR;

export const onAvailabilityError = event => {
  const { plans } = event.getState();
  availabilityTrackError(plans, plansActionTypes.GET_PLANS_AVAILABILITY_INFO_ERROR, ROOM_RATES_ERROR_MSG);
  return event;
};
onAvailabilityError.eventType = plansActionTypes.GET_PLANS_AVAILABILITY_INFO_ERROR;

export const onDetailedAvailabilityError = event => {
  const { suite } = event.getState();
  availabilityTrackError(suite, suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_ERROR, ROOM_RATES_ERROR_MSG, true);
  return event;
};
onDetailedAvailabilityError.eventType = suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_ERROR;

export const onDetailedAvailabilitySuccess = event => {
  const { suite } = event.getState();
  const personalizationPackageRules = get(suite, 'personalizationPackageRules');
  if (personalizationPackageRules && personalizationPackageRules.length > 0) {
    const interactionsData = getPackageInteractionsData(suite);
    if (interactionsData && interactionsData.length > 0) {
      interactionsData.forEach(recommendation => {
        pushEvent({
          event: EVENT_CATEGORIES.personalizationRules,
          rules: personalizationPackageRules.join(),
          rec: recommendation.recommendationEngine,
          recType: recommendation.recommendationType,
          recValue: recommendation.packageCode
        });
      });
    } else {
      pushEvent({
        event: EVENT_CATEGORIES.personalizationRules,
        rules: personalizationPackageRules.join()
      });
    }
  }
};
onDetailedAvailabilitySuccess.eventType = suiteActionTypes.SET_SUITE_PERSONALIZATION_PACKAGES;

export const onSuiteSelect = ({ type, eventProps, getState, selectedSuite, upsellRecommendationData }) => {
  if (type !== suiteActionTypes.SELECT_SUITE || !selectedSuite) return;
  const { dates, currentLodge, guests, router } = getState();
  const currentPathLocation = router.location.pathname || '';
  const purchaseLocation = eventProps?.customPurchaseLocation
    ? eventProps?.customPurchaseLocation
    : getCurrentLocation(currentPathLocation);
  const resortLocation = getResortLocation(currentLodge);
  const position = eventProps?.currentPosition + 1;

  pushEvent(
    buildProductClickEnhancedECommerceObject(
      guests.kidsAges,
      selectedSuite,
      resortLocation,
      dates.checkoutDateSelection,
      dates.checkinDateSelection,
      guests.adultsCount,
      guests.kidsCount,
      position,
      purchaseLocation
    )
  );

  pushEvent(
    buildAddToCartEnhancedEcommerceObject(
      GTM_EVENTS.GTM.addToCart,
      guests.kidsAges,
      selectedSuite,
      resortLocation,
      dates.checkoutDateSelection,
      dates.checkinDateSelection,
      guests.adultsCount,
      guests.kidsCount,
      position,
      purchaseLocation,
      upsellRecommendationData
    )
  );
};

export const onIsCartOpenClose = ({ type, getState }) => {
  if (type !== 'SET_PLANS_IS_CART_OPEN_CLOSE') return;
  const { plans } = getState();
  const clickType = !plans.isCartOpenClose ? 'cart opened' : 'cart closed';
  pushEvent({
    event: EVENT_CATEGORIES.customClick,
    click_category: 'cart click',
    click_type: clickType
  });
};

export const onCartReturnedPageLinkClicked = ({ type, getState }) => {
  if (type !== 'SET_PLANS_CART_RETURNED_PAGE_LINK_CLICKED_CLEAN') return;
  const { plans } = getState();
  const clickType = plans.cartReturnedPageLinkClicked;
  pushEvent({
    event: EVENT_CATEGORIES.customClick,
    click_category: 'cart_link_click',
    click_type: clickType
  });
};

export const onSearchClick = ({ type, getState }) => {
  if (type !== 'GET_PLANS_AVAILABILITY_INFO_SUCCESS') return;

  const { dates, guests, router, offer, account, plans } = getState();
  const currentPathLocation = router.location.pathname || '';
  const searchLocation = getCurrentLocation(currentPathLocation);
  const bonusDealValue = () => {
    let valueReturned = 0 + account?.currentLoggedInUser?.pointsBalance;
    account?.currentLoggedInUser?.offerList?.forEach(offer => {
      valueReturned = valueReturned + offer.dollarValue;
    });
    return (!isNaN(valueReturned) ? valueReturned : 0) + ' points';
  };

  const isErrorCodeSearch = getPromoSearchErrorCode(plans);
  const arrivalDate = GwDatesWrapper.format(dates?.checkinDateSelection, DATE_FORMATS.default);
  const departureDate = GwDatesWrapper.format(dates?.checkoutDateSelection, DATE_FORMATS.default);
  const isPromoBannerSearch = getIsOfferAppliedViaPromoBanner(offer ?? {});
  const searchType = isPromoBannerSearch ? SEARCH_TYPES.promoSearch : SEARCH_TYPES.reservation;
  const promoBannerPersonalization = isPromoBannerSearch ? getOfferPromoBannerPersonalization(offer) : null;
  const resortLocationName = getResortShortName();

  const eventObject =
    isErrorCodeSearch?.promoOfTheDay === offer?.code
      ? getPromoErrorEventObject(
          resortLocationName,
          arrivalDate,
          departureDate,
          guests?.adultsCount,
          guests?.kidsAges,
          offer?.code
        )
      : {
          ...buildSearchObject(
            resortLocationName,
            searchType,
            bonusDealValue(),
            searchLocation,
            arrivalDate,
            departureDate,
            guests?.adultsCount,
            guests?.kidsCount,
            guests?.kidsAges,
            offer?.code
          ),
          ...promoBannerPersonalization
        };
  pushEvent(eventObject);
};

const EVENT_CATEGORIES = {
  personalizationRules: 'Personalization Rules',
  customClick: 'custom_click'
};
